import parseJson from "../util/parseJson";

const MESSAGE_EVENT = "message";

function isAnObject(something: unknown): boolean {
    return typeof something === "object";
}

function destructureEvent(event: MessageEvent) {
    const data = isAnObject(event.data) ? event.data : parseJson(event.data, {});
    const id = data.id || data.windowId;
    const method = data.method;

    return { data, id, method };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Cb = (data?: any) => void;

/**
 * Façade for registering listeners to messages posted to window
 *
 * @class
 */
class PostMessages {

    _listeners: Record<string, Record<string, Cb[]>> = {};

    constructor() {

        this._contextifyCallbacks();
        this._delegateEvents();
    }

    /**
     * Bind methods to self for use as callbacks
     *
     * @private
     */
    _contextifyCallbacks() {
        this._dispatchEventListeners = this._dispatchEventListeners.bind(this);
    }

    /**
     * Registers global event listener for message event
     *
     * @private
     */
    _delegateEvents() {
        window.addEventListener(MESSAGE_EVENT, this._dispatchEventListeners);
    }

    /**
     * Dispatches callbacks with method and id from event data
     */
    _dispatchEventListeners(event: MessageEvent) {
        const { data, id, method } = destructureEvent(event);
        const callbacks = (this._listeners[id] ? this._listeners[id][method] : []) || [];

        callbacks.forEach((callback) => callback(data));
    }

    /**
     * Tears down global event listener for visibility change
     *
     * @private

     */
    _undelegateEvents() {
        window.removeEventListener(MESSAGE_EVENT, this._dispatchEventListeners);
    }

    /**
     * Destructor
     */
    destroy() {
        this._undelegateEvents();
        this._listeners = {};
    }

    /**
     * Add post message listener to id and method
     * @example
     * postMessages.addEventListener("1", "setIframeHeight", function(data) {
     *     iframe.setAttribute("height", data.height);
     * });
     */
    addEventListener(id: string, method: string, callback: Cb) {
        this._listeners[id] = this._listeners[id] || {};

        const listenersForId = this._listeners[id];

        listenersForId[method] = listenersForId[method] || [];
        listenersForId[method].push(callback);
    }

    /**
     * Remove post message listener from id and method
     * @example
     * postMessages.removeEventListener("1", "setIframeHeight", setIframeHeight);
     */
    removeEventListener(id: string, method: string, callback: Cb) {
        const listenersForId = this._listeners[id];

        if (listenersForId && listenersForId[method]) {
            listenersForId[method] = listenersForId[method].filter((item) => item !== callback);
        }
    }

}

export default PostMessages;
